import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Factory, MapPin, ShieldCheck, Clock, Ruler, Truck } from "lucide-react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { getLocationBySlug, getStateBySlug } from "../../data/locations";
import { business } from "../../config/business";

const approach = [
  { icon: ShieldCheck, title: "Design at Our Facility", text: "Every structure is engineered and detailed at our Hyderabad facility to the site's specific span, height and load requirements." },
  { icon: Ruler, title: "Site-Specific Load Design", text: "Wind and seismic loads are calculated for your exact site location, not reused from a generic template." },
  { icon: Factory, title: "Factory Fabrication", text: "Steel is cut, drilled and coated under controlled shop conditions before it ever reaches your site." },
  { icon: Truck, title: "Delivery & On-Site Erection", text: "We manage transport logistics and erection through to a handover-ready structure." },
];

const CityPage = () => {
  const { stateSlug, citySlug } = useParams();
  const city = getLocationBySlug(citySlug);
  const state = getStateBySlug(stateSlug);

  if (!city || !state || city.stateSlug !== stateSlug) {
    return <Navigate to="/locations" replace />;
  }

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "PEB and industrial shed design, fabrication and erection",
    provider: {
      "@type": "Organization",
      name: business.name,
      telephone: business.phone.display,
      email: business.email.display,
      address: business.address.line,
    },
    areaServed: { "@type": "City", name: city.name },
    description: city.intro,
  };

  return (
    <Layout>
      <SEOHead
        title={`PEB Manufacturer in ${city.name} | Vasavi Structures`}
        description={`Vasavi Structures designs and fabricates PEB buildings and industrial sheds for ${city.name}, ${state.name}, with on-site erection from our Hyderabad facility.`}
        keywords={`PEB manufacturer ${city.name}, industrial shed manufacturer ${city.name}, steel fabrication ${city.name}, PEB structures ${city.name}, warehouse construction ${city.name}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>
      <Breadcrumb
        items={[
          { name: "Locations", path: "/locations" },
          { name: state.name, path: `/locations/${state.slug}` },
          { name: city.name },
        ]}
      />

      {/* 1. Hero */}
      <section className="section-padding pb-10">
        <div className="container-custom">
          <div className="flex items-center gap-2 text-secondary mb-4">
            <MapPin size={20} aria-hidden="true" />
            <span className="font-montserrat font-semibold">{city.distance}</span>
          </div>
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
            PEB Buildings & Industrial Sheds in <span className="text-secondary">{city.name}</span>
          </h1>
          <p className="text-muted-foreground max-w-3xl text-lg">{city.intro}</p>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Site-specific structural design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fabrication & foundation run in parallel</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Transport & erection managed end to end</span>
          </div>
        </div>
      </section>

      {/* 3. Industrial zones + sectors */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-montserrat text-xl md:text-2xl font-bold text-foreground mb-4">
                Industrial Zones Near {city.name}
              </h2>
              <ul className="space-y-2">
                {city.industrialZones.map((zone) => (
                  <li key={zone} className="flex items-start gap-2 text-muted-foreground">
                    <Factory size={18} className="text-secondary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    {zone}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-montserrat text-xl md:text-2xl font-bold text-foreground mb-4">
                Key Industries in the Region
              </h2>
              <div className="space-y-3">
                {city.sectors.map((sector) => (
                  <div key={sector.name} className="p-4 bg-card rounded-lg border border-border">
                    <h3 className="font-montserrat font-semibold text-foreground">{sector.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{sector.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. How we deliver — process */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            How We Deliver to {city.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {approach.map((step, i) => (
              <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="text-primary" size={22} aria-hidden="true" />
                </div>
                <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Related services */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6 text-center">
            Services Available in {city.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "PEB Buildings", path: "/pre-fab-structure-buildings" },
              { name: "Industrial Sheds", path: "/industrial-sheds" },
              { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
              { name: "Roof & Wall Sheeting", path: "/roof-wall-sheeting" },
            ].map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="p-4 bg-muted rounded-lg text-center font-montserrat font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition"
              >
                {service.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={city.faqs} title={`${city.name} — Frequently Asked Questions`} />

      {/* 7. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Get a Quote in {city.name}
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Tell us about your project and we'll respond with a design consultation and estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="btn-outline border-secondary-foreground text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary inline-block px-8 py-4">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CityPage;
